// Printer Proxy Service for handling HTTPS mixed content issues
// This service provides methods to detect and test printers via backend proxy

class PrinterProxyService {
    constructor(baseURL = '/api/printer-proxy') {
        this.baseURL = baseURL;
    }

    /**
     * Test a single printer IP via backend proxy
     * @param {string} ip - IP address to test
     * @param {number} timeout - Timeout in milliseconds
     * @returns {Promise<Object|null>} Printer info or null
     */
    async testSinglePrinter(ip, timeout = 3000) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout + 1000); // Add buffer for network delay

        try {
            const response = await fetch(`${this.baseURL}/test-single`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ip, timeout }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            
            if (result.success && result.printer) {
                return {
                    ...result.printer,
                    name: `Printer-${ip}`,
                    environment: 'production-proxy',
                    proxyUsed: true
                };
            }

            return null;
        } catch (error) {
            clearTimeout(timeoutId);
            console.warn(`Proxy test failed for ${ip}:`, error.message);
            return null;
        }
    }

    /**
     * Scan multiple IP ranges via backend proxy
     * @param {Array<string>} ipRanges - Array of IP prefixes (e.g., ['192.168.1.', '192.168.0.'])
     * @param {Object} options - Scan options
     * @returns {Promise<Array>} Array of found printers
     */
    async scanIPRanges(ipRanges = ['192.168.1.', '192.168.0.'], options = {}) {
        const {
            startRange = 1,
            endRange = 254,
            timeout = 3000,
            batchSize = 10
        } = options;

        try {
            const response = await fetch(`${this.baseURL}/scan-ranges`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ipRanges,
                    startRange,
                    endRange,
                    timeout,
                    batchSize
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            
            if (result.success) {
                return result.printers.map(printer => ({
                    ...printer,
                    name: `Printer-${printer.ip}`,
                    environment: 'production-proxy',
                    proxyUsed: true
                }));
            }

            return [];
        } catch (error) {
            console.error('Proxy scan failed:', error.message);
            throw error;
        }
    }

    /**
     * Get backend proxy health status
     * @returns {Promise<boolean>} True if proxy is available
     */
    async checkProxyHealth() {
        try {
            const response = await fetch(`${this.baseURL}/health`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            return response.ok;
        } catch (error) {
            console.warn('Proxy health check failed:', error.message);
            return false;
        }
    }

    /**
     * Test printer connection via backend proxy
     * @param {string} ip - Printer IP
     * @param {number} port - Printer port
     * @returns {Promise<Object>} Connection test result
     */
    async testConnection(ip, port = 8008) {
        try {
            const response = await fetch(`${this.baseURL}/test-connection`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ip, port })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Connection test via proxy failed:', error.message);
            return { success: false, error: error.message };
        }
    }

    /**
     * Send print job via backend proxy (for production HTTPS)
     * @param {string} ip - Printer IP
     * @param {number} port - Printer port
     * @param {string} xmlData - ePOS-Print XML data
     * @param {string} method - Print method (epos-print or raw-9100)
     * @returns {Promise<Object>} Print result
     */
    async printViaProxy(ip, port = 8008, xmlData, method = 'epos-print') {
        try {
            const response = await fetch(`${this.baseURL}/print`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ip, port, xmlData, method })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Print via proxy failed:', error.message);
            return { success: false, error: error.message };
        }
    }

    /**
     * Open cash drawer via backend proxy (for production HTTPS)
     * @param {string} ip - Printer IP
     * @param {number} port - Printer port
     * @param {string} method - Connection method
     * @returns {Promise<Object>} Drawer operation result
     */
    async openDrawerViaProxy(ip, port = 8008, method = 'epos-print') {
        try {
            const response = await fetch(`${this.baseURL}/open-drawer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ip, port, method })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Drawer operation via proxy failed:', error.message);
            return { success: false, error: error.message };
        }
    }

    /**
     * Check if we're in production (HTTPS) environment
     * @returns {boolean} True if in production
     */
    isProduction() {
        return window.location.protocol === 'https:';
    }

    /**
     * Enhanced print function that automatically uses proxy in production
     * @param {string} ip - Printer IP
     * @param {number} port - Printer port
     * @param {string} xmlData - ePOS-Print XML data
     * @param {string} method - Print method
     * @returns {Promise<Object>} Print result
     */
    async print(ip, port = 8008, xmlData, method = 'epos-print') {
        if (this.isProduction()) {
            console.log('Production environment detected, using proxy for print...');
            return await this.printViaProxy(ip, port, xmlData, method);
        } else {
            console.log('Development environment, attempting direct print...');
            try {
                // Try direct connection first in development
                const printUrl = method === 'epos-print' && port === 8008
                    ? `http://${ip}:${port}/epos/print`
                    : `http://${ip}:${port}/epos/print`;

                const response = await fetch(printUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'text/xml; charset=utf-8',
                        'SOAPAction': ''
                    },
                    body: xmlData,
                    mode: 'no-cors'
                });

                return { success: true, message: 'Print sent directly', directConnection: true };
            } catch (error) {
                console.log('Direct print failed, falling back to proxy...');
                return await this.printViaProxy(ip, port, xmlData, method);
            }
        }
    }

    /**
     * Enhanced drawer function that automatically uses proxy in production
     * @param {string} ip - Printer IP
     * @param {number} port - Printer port
     * @param {string} method - Connection method
     * @returns {Promise<Object>} Drawer operation result
     */
    async openDrawer(ip, port = 8008, method = 'epos-print') {
        if (this.isProduction()) {
            console.log('Production environment detected, using proxy for drawer...');
            return await this.openDrawerViaProxy(ip, port, method);
        } else {
            console.log('Development environment, attempting direct drawer operation...');
            try {
                // Try direct connection first in development
                const drawerXml = `<?xml version="1.0" encoding="utf-8"?>
<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
    <pulse drawer="drawer_1" time="pulse_100"/>
</epos-print>`;

                const printUrl = method === 'epos-print' && port === 8008
                    ? `http://${ip}:${port}/epos/print`
                    : `http://${ip}:${port}/epos/print`;

                const response = await fetch(printUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'text/xml; charset=utf-8',
                        'SOAPAction': ''
                    },
                    body: drawerXml,
                    mode: 'no-cors'
                });

                return { success: true, message: 'Drawer opened directly', directConnection: true };
            } catch (error) {
                console.log('Direct drawer operation failed, falling back to proxy...');
                return await this.openDrawerViaProxy(ip, port, method);
            }
        }
    }
}

// Export singleton instance
export const printerProxyService = new PrinterProxyService();
export default PrinterProxyService;